import React, { FC, Fragment } from "react";
import classnames from "classnames";

import AddProperty from "./AddProperty";
import FormattedSchemaPath from "./FormattedSchemaPath";
import LoadingIndicator from "src/components/general/LoadingIndicator";
import SchemaDescription from "src/components/index/schemas/SchemaDescription";
import SchemaLink from "src/components/index/schemas/SchemaLink";
import TrashIcon from "src/components/icons/TrashIcon";
import {
  Property,
  SchemaReference,
  UpdateSubjectMutation,
  UpdateSubjectMutationVariables,
} from "src/lib/graphql/types";
import { schemaPath } from "src/lib/schemaOrgUrl";
import { uniqueSchemaReferences } from "src/lib/schemaNames";
import { updateSubjectMutation } from "src/lib/graphql/schemas.queries";
import { useMutation } from "@apollo/client";

const SchemaPropertyExpectedTypeSeparator: FC<{
  idx: number;
  types: SchemaReference[];
}> = ({ idx, types }) => {
  if (!types[idx + 1]) {
    return null;
  } else if (!types[idx + 2]) {
    return <> or&nbsp;</>;
  }

  return <>,&nbsp;</>;
};

const SchemaPropertiesTable: FC<{
  canUserEdit?: boolean;
  name: string;
  namespace: string;
  properties: Pick<
    Property,
    "description" | "expectedType" | "name" | "namespace"
  >[];
  primary?: boolean;
  version?: string;
}> = ({
  canUserEdit,
  name,
  namespace,
  properties,
  primary = false,
  version,
}) => {
  const SchemaName = <FormattedSchemaPath name={name} namespace={namespace} />;
  const classNames = classnames("schema-property-table", { primary });

  const [updateSubject, { loading }] = useMutation<
    UpdateSubjectMutation,
    UpdateSubjectMutationVariables
  >(updateSubjectMutation);

  const updateProperties = (newProperties: SchemaReference[]) => {
    updateSubject({
      variables: {
        name,
        namespace,
        // don't send unneeded fields if we have fuller Property objects
        properties: newProperties.map(({ name, namespace, version }) => ({
          name,
          namespace,
          version,
        })),
        version,
      },
    });
  };

  const addProperty = (property: SchemaReference) => {
    const newProperties = uniqueSchemaReferences([property, ...properties]);
    updateProperties(newProperties);
  };

  const deleteProperty = (property: SchemaReference) => {
    const newProperties = properties.filter(
      ({ name, namespace }) =>
        !(property.name === name && property.namespace && namespace)
    );
    updateProperties(newProperties);
  };

  return (
    <>
      <table className={classNames}>
        <thead>
          <tr>
            <th>
              {primary ? (
                <>Properties of {SchemaName}</>
              ) : (
                <>
                  Properties inherited from{" "}
                  <SchemaLink
                    name={name}
                    namespace={namespace}
                    href={schemaPath({ name, namespace })}
                  >
                    {SchemaName}
                  </SchemaLink>
                </>
              )}
            </th>
            <th>Expected Type</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          {properties.map(({ description, expectedType, name, namespace }) => (
            <tr key={name}>
              <td>
                <SchemaLink
                  name={name}
                  namespace={namespace}
                  href={schemaPath({ name, namespace })}
                >
                  <FormattedSchemaPath name={name} namespace={namespace} />
                </SchemaLink>
                {canUserEdit && (
                  <button
                    className="delete-property"
                    onClick={() => deleteProperty({ name, namespace })}
                  >
                    <TrashIcon />
                  </button>
                )}
              </td>
              <td>
                {expectedType.map(({ name, namespace }, idx, types) => (
                  <Fragment key={`@${name}/${namespace}`}>
                    <div className="soft-break">
                      <SchemaLink
                        name={name}
                        namespace={namespace}
                        tooltip
                        href={schemaPath({ name, namespace })}
                      >
                        <FormattedSchemaPath
                          name={name}
                          namespace={namespace}
                        />
                      </SchemaLink>
                    </div>
                    <SchemaPropertyExpectedTypeSeparator
                      types={types}
                      idx={idx}
                    />
                  </Fragment>
                ))}
              </td>
              <td>
                <SchemaDescription description={description} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {canUserEdit && (
        <div className="add-property-form-container">
          {loading ? (
            <LoadingIndicator loading />
          ) : (
            <AddProperty
              addProperty={addProperty}
              schema={{ name, namespace, version }}
            />
          )}
        </div>
      )}
    </>
  );
};

export default SchemaPropertiesTable;
